import React, { useMemo } from 'react'
import { Button, Text, Grid, Popover, PopoverTrigger, PopoverContent, PopoverBody, Box } from '@chakra-ui/core'
import { Editor } from '@tiptap/react'
import { TEXT_COLORS, HIGHLIGHT_COLORS } from './constants'

interface ColorPickerProps {
  editor: Editor
  type: 'text' | 'highlight'
  isOpen: boolean
  onClose: () => void
  onToggle: () => void
  isActive: boolean
  children: React.ReactNode
}

/**
 * ColorPicker - color selection component using Chakra UI Popover
 */
export const ColorPicker: React.FC<ColorPickerProps> = ({
  editor,
  type,
  isOpen,
  onClose,
  onToggle,
  isActive,
  children,
}) => {
  // Select color palette based on type
  const colors = useMemo(() => (type === 'text' ? TEXT_COLORS : HIGHLIGHT_COLORS), [type])

  // Handle color selection
  const handleColorSelect = (color: string) => {
    if (type === 'text') {
      editor.chain().focus().setColor(color).run()
    } else {
      editor.chain().focus().toggleHighlight({ color }).run()
    }
    onClose()
  }

  // Handle color removal
  const handleRemoveColor = () => {
    if (type === 'text') {
      editor.chain().focus().unsetColor().run()
    } else {
      editor.chain().focus().unsetHighlight().run()
    }
    onClose()
  }

  return (
    <Popover
      isOpen={isOpen}
      onClose={onClose}
      placement="bottom-start"
      closeOnBlur={true}
      closeOnEsc={true}
      usePortal={true}
    >
      <PopoverTrigger>
        <Box
          as="button"
          onClick={onToggle}
          className={`toolbar-button ${isActive ? 'active' : ''}`}
          aria-label={type === 'text' ? 'Text Color' : 'Highlight Color'}
        >
          {children}
        </Box>
      </PopoverTrigger>
      <PopoverContent zIndex={100000} width="200px" _focus={{ outline: 'none' }}>
        <PopoverBody p={3}>
          <Text
            fontSize="xs"
            fontWeight="semibold"
            color="gray.600"
            mb={2}
            textTransform="uppercase"
            letterSpacing="wider"
          >
            {type === 'text' ? 'Text Color' : 'Highlight Color'}
          </Text>

          {/* Color grid */}
          <Grid templateColumns="repeat(4, 1fr)" gap={1.5}>
            {colors.map((color) => (
              <Button
                key={color.value}
                onClick={() => handleColorSelect(color.value)}
                aria-label={color.name}
                title={color.name}
                width="36px"
                height="36px"
                minWidth="36px"
                borderRadius="6px"
                border="2px solid"
                borderColor="gray.200"
                backgroundColor={color.value}
                p={0}
                _hover={{
                  transform: 'scale(1.1)',
                  borderColor: 'blue.400',
                }}
                transition="all 0.2s"
              >
                {' '}
              </Button>
            ))}
          </Grid>

          {/* Remove color button */}
          <Button
            onClick={handleRemoveColor}
            mt={3}
            width="100%"
            size="sm"
            fontSize="xs"
            fontWeight="medium"
            color="gray.600"
            bg="gray.50"
            border="1px solid"
            borderColor="gray.200"
            borderRadius="6px"
            _hover={{
              bg: 'gray.100',
            }}
            transition="all 0.2s"
          >
            Remove {type === 'text' ? 'Color' : 'Highlight'}
          </Button>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  )
}
